/**
 * The "Reference code" shown on a crash screen, and the rules for when it may
 * be shown at all (#3253). Used by BOTH crash screens — `app/error.tsx` (the
 * route-segment boundary, which handles every route) and `app/global-error.tsx`
 * (root-layout crashes only). They must behave identically: the assistant's
 * `page_error_reference_code` entry describes one behaviour, and a user cannot
 * tell which of the two screens they are looking at.
 */

import * as Sentry from '@sentry/nextjs';

/**
 * The crash-screen copy, shared by app/error.tsx and app/global-error.tsx.
 *
 * Exported rather than duplicated because both files' comments CLAIM the two
 * screens show the same words, and a comment asserting parity is a claim that
 * needs enforcing — otherwise the next copy edit silently applies to one screen
 * and the assistant's single description becomes wrong for the other.
 *
 * Deliberately makes no promise that the crash reached us: an ad blocker or
 * strict privacy setting stops the report while the code still renders, so
 * "we've been told" would be false for exactly the users most likely to hit it.
 * "we'll dig into it" is a promise about what happens once they get in touch,
 * which is true in every case.
 */
export const CRASH_SCREEN_HEADING = 'Something went wrong';

export const CRASH_SCREEN_BODY_WITH_CODE =
  "This page didn't load — that one's on us, not you. Give it another go, and if it sticks around, send support the code below and we'll dig into it.";

export const CRASH_SCREEN_BODY_NO_CODE =
  "This page didn't load — that one's on us, not you. Give it another go, and if it sticks around, get in touch with support and we'll dig into it.";

/**
 * Whether an event captured right now could actually have been sent.
 *
 * `Sentry.getClient()` alone is NOT this check: `Sentry.init()` runs
 * unconditionally at module scope and `@sentry/core`'s `initAndBind` always
 * constructs and binds a client — even with `enabled: false` or no DSN. This
 * mirrors the SDK's own private `_isEnabled()` (core/client.js:679):
 *
 *   getOptions().enabled !== false && transport !== undefined
 *
 * which covers a non-production build (`enabled: false`) and a missing DSN.
 *
 * It CANNOT cover a browser add-on blocking the ingest domain — that failure is
 * asynchronous and swallowed — so neither the screens nor the assistant may
 * claim that a MISSING code implies an ad-blocker. The opposite is true there:
 * the code renders and is simply unfindable afterwards.
 */
export function isSentryReporting(): boolean {
  const client = Sentry.getClient();
  return (
    !!client &&
    client.getOptions().enabled !== false &&
    !!client.getTransport()
  );
}

/**
 * Captures the crash and returns the code to show the user, or null when there
 * is nothing findable to show.
 *
 * The `nextDigest` tag is what makes a SERVER crash findable. Next.js sets
 * `digest` only for server-side errors, and neither the Sentry SDK nor
 * `captureRequestError` indexes it anywhere — no tag, no context, no
 * fingerprint (verified against @sentry/nextjs 10.44). `src/instrumentation.ts`
 * sets the SAME tag on the server-side capture, so searching
 * `nextDigest:<code>` returns both events: the server one with the real stack,
 * and this client one whose message Next masks. Without both halves the code
 * resolves only to the masked event, which is not "the specific failure".
 */
export function captureCrashAndGetReference(
  error: Error & { digest?: string }
): string | null {
  const eventId = Sentry.captureException(error, {
    tags: { nextDigest: error.digest || 'none' },
  });

  if (!isSentryReporting()) return null;
  return error.digest || eventId || null;
}
